import React, { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import {
  Row,
  Col,
  Form,
  Input,
  Radio,
  Select,
  Tooltip,
  Checkbox,
  InputNumber,
} from "antd";

import {
  get,
  checkHttpStatus,
  checkResponseGet,
} from "../../../modules/httpFunctions";
import { handleError } from "../../../modules/errorLogger";

const { Item } = Form;
const { Option } = Select;

function ImportForm({ form, showColliDelimiter, importDefaultSetting }) {
  const { getFieldDecorator } = form;
  const formItemLayout = {
    labelCol: {
      span: 7,
    },
    wrapperCol: {
      span: 17,
    },
    className: "import-form",
    colon: false,
  };
  const intl = useIntl();

  const [encodings, setEncodings] = useState([]);
  const [delimiters, setDelimiters] = useState([]);
  const loadEncodings = useCallback(() => {
    get("setting/import")
      .then((r) => {
        if (checkHttpStatus(r) && checkResponseGet(r)) {
          setEncodings(r.data.data.encodings);
          setDelimiters(r.data.data.delimiters);
        } else {
          setEncodings([]);
          setDelimiters([]);
        }
      })
      .catch((err) => {
        setEncodings([]);
        setDelimiters([]);
        handleError(err);
      });
  }, []);

  useEffect(() => {
    loadEncodings();
  }, [loadEncodings, intl.locale]);

  return (
    <Form {...formItemLayout}>
      <Row>
        <Col span={8}>
          <Item
            label={intl.formatMessage({
              id: "import.fileType",
              defaultMessage: "Typ souboru",
            })}
            labelCol={{ span: 13 }}
            wrapperCol={{ span: 11 }}
          >
            {getFieldDecorator("fileType", {
              initialValue: importDefaultSetting.fileType,
            })(
              <Radio.Group>
                <Radio value="xlsx">xlsx</Radio>
                <Radio value="csv">csv</Radio>
              </Radio.Group>
            )}
          </Item>
        </Col>
      </Row>
      <Row>
        <Col span={5}>
          <Item
            labelCol={{ span: 21 }}
            wrapperCol={{ span: 3 }}
            label={intl.formatMessage({
              id: "import.containsHeader",
              defaultMessage: "Soubor obsahuje hlavičku",
            })}
          >
            {getFieldDecorator("containHeader", {
              initialValue: importDefaultSetting.containHeader,
              valuePropName: "checked",
            })(<Checkbox />)}
          </Item>
        </Col>
        {form.getFieldValue("fileType") === "xlsx" ? (
          <Col span={19}>
            <Row>
              <Col span={7}>
                <Item
                  label={intl.formatMessage({
                    id: "import.workbookNUmber",
                    defaultMessage: "Číslo sešitu",
                  })}
                  labelCol={{ span: 14 }}
                  wrapperCol={{ span: 10 }}
                >
                  {getFieldDecorator("worksheetNumber", {
                    initialValue: importDefaultSetting.worksheetNumber,
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "import.emptyWorkbookNUmber",
                          defaultMessage: "Vyplňte prosím číslo sešitu!",
                        }),
                      },
                    ],
                  })(<InputNumber min={1} max={999} precision={0} />)}
                </Item>
              </Col>
              <Col span={8}>
                <Item
                  label={intl.formatMessage({
                    id: "import.filePassword",
                    defaultMessage: "Heslo k souboru",
                  })}
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                >
                  {getFieldDecorator("excelPassword", { initialValue: "" })(
                    <Input.Password />
                  )}
                </Item>
              </Col>
              <Col span={9}>
                {showColliDelimiter && (
                  <Item
                    label={intl.formatMessage({
                      id: "import.colliDelimiter",
                      defaultMessage: "Oddělovač atributů kusu/ů",
                    })}
                    labelCol={{ span: 16 }}
                    wrapperCol={{ span: 8 }}
                  >
                    {getFieldDecorator("colliDelimiter", {
                      initialValue: importDefaultSetting.colliDelimiter,
                      rules: [
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: "import.emptyColliDelimiter",
                            defaultMessage: "Vyplňte prosím oddělovač kusů!",
                          }),
                        },
                      ],
                    })(<Input />)}
                  </Item>
                )}
              </Col>
            </Row>
          </Col>
        ) : (
          <Col span={19}>
            <Row>
              <Col span={12}>
                <Item
                  label={intl.formatMessage({
                    id: "import.coding",
                    defaultMessage: "Kódování",
                  })}
                >
                  {getFieldDecorator("encoding", {
                    initialValue: importDefaultSetting.encoding,
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "import.emptyCoding",
                          defaultMessage: "Vyberte prosím kódování souboru!",
                        }),
                      },
                    ],
                  })(
                    <Select
                      allowClear
                      showSearch
                      filterOption={(inputValue, option) =>
                        inputValue &&
                        option &&
                        option.props &&
                        option.props.children &&
                        option.props.children.toUpperCase &&
                        option.props.children
                          .toUpperCase()
                          .indexOf(inputValue.toUpperCase()) !== -1
                      }
                    >
                      {encodings.map((e, i) => (
                        <Option value={e.codePage} key={i}>
                          <Tooltip title={`${e.name} (${e.codePage})`}>
                            {`${e.name} (${e.codePage})`}
                          </Tooltip>
                        </Option>
                      ))}
                    </Select>
                  )}
                </Item>
              </Col>
              <Col span={10}>
                <Item
                  label={intl.formatMessage({
                    id: "import.delimiter",
                    defaultMessage: "Oddělovač",
                  })}
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                >
                  {getFieldDecorator("delimiter", {
                    initialValue: delimiters.some(
                      (d) => d.name === importDefaultSetting.delimiter
                    )
                      ? importDefaultSetting.delimiter
                      : intl.formatMessage({
                          id: "import.firstDelimiter",
                          defaultMessage: "středník",
                        }),
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "import.emptyDelimiter",
                          defaultMessage: "Vyplňte prosím oddělovač!",
                        }),
                      },
                    ],
                  })(
                    <Select
                      allowClear
                      showSearch
                      filterOption={(inputValue, option) =>
                        inputValue &&
                        option &&
                        option.props &&
                        option.props.children &&
                        option.props.children.toUpperCase &&
                        option.props.children
                          .toUpperCase()
                          .indexOf(inputValue.toUpperCase()) !== -1
                      }
                    >
                      {delimiters.map((e, i) => (
                        <Option value={e.name} key={i}>
                          <Tooltip>{e.name}</Tooltip>
                        </Option>
                      ))}
                    </Select>
                  )}
                </Item>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </Form>
  );
}

export default Form.create()(ImportForm);
